import { motion } from "framer-motion";
import { ShieldAlert, Eye, Ticket, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PageBanner } from "@/components/PageBanner";
import { IMAGES, DISCORD_URL } from "@/data/site";

const ssRules = [
  "Rifiutare il controllo = colpevolezza presunta → sanzione immediata.",
  "Cancellare file, cronologia o prove durante o prima del controllo = sanzione immediata.",
  'Disconnettersi ("crashare") durante il controllo = considerato come rifiuto → sanzione immediata.',
  "Il giocatore è tenuto a collaborare e a seguire le istruzioni dello staff fino al termine della verifica.",
];

const sanctions = [
  ["Spam / Flood / CAPS abusivo", "Mute (da 1h a 24h)", false],
  ["Tossicità / Flame", "Mute → Tempban in caso di recidiva", false],
  ["Pubblicità esterna", "Mute prolungato / Tempban", false],
  ["Bestemmie / Filtri aggirati", "Mute + warning", false],
  ["Razzismo / Omofobia / Discriminazione", "Tempban lungo → Permaban", true],
  ["Uso di Cheat / Client illegali", "Ban 30 giorni → Permaban (recidiva)", true],
  ["X-Ray / Baritone / Auto-Clicker", "Ban 30 giorni → Permaban", true],
  ["Rifiuto/Fuga dal controllo SS", "Ban 30 giorni (trattato come cheating)", true],
  ["Abuso di Bug / Exploit", "Reset progressi + Ban", true],
  ["Lag Machine", "Rimozione struttura + Tempban", false],
  ["RMT (Real Money Trading)", "IP Ban Permanente", true],
  ["Doxxing", "IP Ban Permanente", true],
];

const appealDo = [
  "Accedi al nostro server Discord ufficiale.",
  'Vai nella sezione #apri-ticket e crea un ticket di tipo "Ricorso Sanzione".',
  "Fornisci: nickname esatto, data e tipo di sanzione, e una spiegazione chiara e rispettosa.",
  "Allega eventuali prove (video, screenshot) a tuo favore.",
  "Attendi la risposta dello staff con pazienza.",
];

const appealDont = [
  "Non contestare la sanzione in chat pubblica o in altri canali.",
  "Non aprire ticket multipli o taggare/insultare lo staff.",
  "Non usare toni aggressivi: un ricorso maleducato verrà chiuso immediatamente.",
];

export default function Staff() {
  return (
    <div data-testid="staff-page">
      <PageBanner
        image={IMAGES.staff}
        icon={ShieldAlert}
        title="🛡️ Staff & Sanzioni"
        subtitle="I Guardiani della Giungla"
        text="Il nostro staff è il cuore pulsante della sicurezza del server. Un team adulto, imparziale e addestrato, il cui unico obiettivo è garantire che La Giungla SMP resti un ambiente leale, sicuro e divertente per tutti."
      />

      {/* SCREEN SHARE */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start mb-10">
          <div className="w-16 h-16 rounded-full border-2 border-[#d4af37] flex items-center justify-center gold-glow bg-[#0f1410] shrink-0">
            <Eye className="w-8 h-8 text-[#d4af37]" />
          </div>
          <div>
            <h2 className="font-heading font-bold uppercase text-2xl md:text-3xl text-white mb-3">
              La Procedura di Screen Share (SS)
            </h2>
            <p className="text-[#c8d3cb] leading-relaxed">
              Lo Screen Share è il nostro principale strumento di controllo
              anti-cheat. Quando un membro dello staff ha il fondato sospetto che
              un giocatore stia utilizzando software illeciti, può richiedere un
              controllo diretto del sistema del giocatore.
            </p>
          </div>
        </div>

        <div className="bg-[#141b15] border border-[#1b4d22]/50 p-6 md:p-8 rounded-lg">
          <p className="font-heading uppercase tracking-wide text-[#d4af37] mb-4">
            Durante un controllo SS valgono regole ferree:
          </p>
          <ul className="space-y-3">
            {ssRules.map((r, i) => (
              <li
                key={i}
                data-testid={`ss-rule-${i}`}
                className="flex gap-3 text-[#c8d3cb] text-sm md:text-base leading-relaxed"
              >
                <AlertTriangle className="w-5 h-5 text-[#d4af37] shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-[#e0e0e0] italic text-sm">
            La collaborazione trasparente è l'unico modo per dimostrare la propria
            correttezza. Chi non ha nulla da nascondere, non ha nulla da temere.
          </p>
        </div>
      </section>

      {/* TABELLA SANZIONI */}
      <section className="bg-[#0c110d] border-y border-[#1b4d22]/40 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <h2 className="font-heading font-bold uppercase text-2xl md:text-4xl text-white text-center mb-3">
            📋 Tabella delle Sanzioni
          </h2>
          <p className="text-center text-[#e0e0e0] max-w-2xl mx-auto mb-10 text-sm md:text-base">
            Le sanzioni sono progressive e a discrezione dello staff in base alla
            gravità e alla recidività. La tabella seguente è indicativa.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="overflow-x-auto border border-[#1b4d22] rounded-lg"
            data-testid="sanctions-table"
          >
            <table className="w-full text-left border-collapse min-w-[520px]">
              <thead>
                <tr className="bg-[#1b4d22]">
                  <th className="font-heading uppercase tracking-wider text-[#d4af37] px-5 py-4 text-sm">
                    Infrazione
                  </th>
                  <th className="font-heading uppercase tracking-wider text-[#d4af37] px-5 py-4 text-sm">
                    Sanzione applicata
                  </th>
                </tr>
              </thead>
              <tbody>
                {sanctions.map(([inf, san, grave], i) => (
                  <tr
                    key={i}
                    data-testid={`sanction-row-${i}`}
                    className={`${
                      i % 2 === 0 ? "bg-[#0f1410]" : "bg-[#152018]"
                    } border-b border-[#1b4d22]/30 hover:bg-[#1b4d22]/20 transition-colors`}
                  >
                    <td className="px-5 py-4 text-[#e0e0e0] text-sm flex items-center gap-2">
                      {grave && (
                        <AlertTriangle className="w-4 h-4 text-[#d4af37] shrink-0" />
                      )}
                      {inf}
                    </td>
                    <td className="px-5 py-4 text-[#c8d3cb] text-sm">{san}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* RICORSI */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Accordion type="single" collapsible>
          <AccordionItem
            value="ricorsi"
            data-testid="appeals-accordion"
            className="border border-[#1b4d22]/50 bg-[#141b15] px-5 rounded-lg data-[state=open]:border-[#d4af37]/60"
          >
            <AccordionTrigger className="hover:no-underline py-5">
              <span className="flex items-center gap-3 font-heading font-bold uppercase text-lg md:text-xl text-white">
                <Ticket className="w-6 h-6 text-[#d4af37]" />
                📨 Ricorsi e Contestazioni
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <p className="text-[#c8d3cb] leading-relaxed mb-6">
                Ritieni di aver ricevuto una sanzione ingiusta? Hai il diritto di
                fare ricorso, ma esclusivamente attraverso i canali ufficiali e
                con un comportamento civile.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="font-heading uppercase text-[#d4af37] tracking-wide mb-3 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" /> Come fare ricorso
                  </p>
                  <ol className="space-y-2 list-decimal list-inside text-[#c8d3cb] text-sm">
                    {appealDo.map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ol>
                </div>
                <div>
                  <p className="font-heading uppercase text-[#d4af37] tracking-wide mb-3 text-sm flex items-center gap-2">
                    <XCircle className="w-5 h-5" /> Cosa NON fare
                  </p>
                  <ul className="space-y-2 text-[#c8d3cb] text-sm">
                    {appealDont.map((d, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-[#d4af37]">✕</span> {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-6 text-[#e0e0e0] text-sm italic">
                La decisione presa al termine del ricorso è definitiva.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-10 text-center">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="appeals-discord-btn"
            className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0f1410] hover:bg-[#e6c04a] transition-all duration-300 px-8 py-4 rounded-none uppercase font-heading font-bold tracking-widest shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.6)]"
          >
            <Ticket className="w-5 h-5" />
            Apri un ticket su Discord
          </a>
        </div>
      </section>
    </div>
  );
}
